import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, Field, Input, Select, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatNumber,
  PANEL_SUBSTRATES,
  refusalMessage,
  VirtualPartClient,
  VirtualPartOptionClient,
  type PanelSubstrate,
  type PartGridDTO,
  type VirtualPartDTO,
  type VirtualPartKind,
  type VirtualPartOptionDTO,
} from "../../api/manufacturing";
import { OpenRecord } from "../../components/OpenRecord";
import { NexpimView } from "../../views";
import { PartPicker } from "../manufacturing/PartPicker";

type Translate = (key: string, fallback: string) => string;

const PANELS_KEY = ["manufacturing", "virtual-part"] as const;
const optionsKey = (virtualPartId: number) => ["manufacturing", "virtual-part-option", virtualPartId] as const;

/** A substrate in the plant's words. */
export function substrateLabel(substrate: string, t: Translate): string {
  switch (substrate) {
    case "PARTICLE":
      return t("virtual-part.substrate.particle", "Particleboard");
    case "MDF":
      return t("virtual-part.substrate.mdf", "MDF");
    case "FIBREX":
      return t("virtual-part.substrate.fibrex", "Fibrex");
    case "PLYWOOD":
      return t("virtual-part.substrate.plywood", "Plywood");
    case "HOLLOW_BOARD":
      return t("virtual-part.substrate.hollow-board", "Hollow board");
    default:
      return substrate;
  }
}

/** 0.82 as "82 %". */
function yieldLabel(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return `${formatNumber(Math.round(Number(value) * 1000) / 10)} %`;
}

/**
 * Virtual parts: what engineering BOMs consume when any of several real parts
 * will do. Panels are the one kind so far, consumed by area.
 *
 * <p>A virtual panel is a substrate, a thickness and a finish (particleboard,
 * 16 mm, white), not a sheet anyone buys. A cut part's BOM line takes its
 * area of one; purchasing turns that area into sheets of a real panel at the
 * expected yield. The real panels it can be cut from are its options, and one
 * of them is the default each BOM line starts from.</p>
 *
 * <p>The engineering BOM build creates most of them from SIGM's wood BOMs. A
 * new one is created here from its three keys: the server makes the VIRTUAL
 * part and numbers it. After that only the yield and the default real panel
 * change; one still used by BOM lines cannot be deleted.</p>
 */
function kindLabel(kind: VirtualPartKind, t: Translate): string {
  switch (kind) {
    case "PANEL":
      return t("virtual-part.kind.panel", "Panel");
    case "STRIP":
      return t("virtual-part.kind.strip", "Cornière strip");
    case "FOAM":
      return t("virtual-part.kind.foam", "Foam");
  }
}

export function VirtualPartsPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const allowed = hasView(NexpimView.MANUFACTURING_PARTS);
  const panels = useMemo(() => new VirtualPartClient(http), [http]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const title = t("navigation.item.config-virtual-part", "Virtual parts");

  const list = useQuery({
    queryKey: PANELS_KEY,
    queryFn: () => panels.findAll(),
    enabled: allowed,
  });
  const rows = useMemo(
    () => [...(list.data ?? [])].sort((a, b) => (a.part_number ?? "").localeCompare(b.part_number ?? "")),
    [list.data],
  );
  const selected = rows.find((row) => row.id === selectedId) ?? null;

  const columns = useMemo(() => {
    const c = defineColumns<VirtualPartDTO>();
    return [
      c.text("part_number", (row) => row.part_number ?? "", { headerKey: "column.virtual-part.number", priority: 1 }),
      c.text("part_name", (row) => row.part_name ?? "", { headerKey: "column.name", priority: 2 }),
      c.text("kind", (row) => kindLabel(row.kind, t), { headerKey: "column.virtual-part.kind", filter: "select", priority: 3 }),
      c.text("substrate", (row) => (row.substrate ? substrateLabel(row.substrate, t) : ""), {
        headerKey: "column.virtual-part.substrate",
        filter: "select",
        priority: 2,
      }),
      c.number("thickness_mm", (row) => (row.thickness_mm == null ? null : Number(row.thickness_mm)), { headerKey: "column.virtual-part.thickness", priority: 3 }),
      c.text("finish", (row) => row.finish ?? "", { headerKey: "column.virtual-part.finish", filter: "select", priority: 2 }),
      c.text("expected_yield", (row) => yieldLabel(row.expected_yield), { headerKey: "column.virtual-part.yield", priority: 2 }),
      c.text("default_real_part_number", (row) => row.default_real_part_number ?? "", {
        headerKey: "column.virtual-part.default-real",
        priority: 3,
      }),
      c.number("option_count", (row) => row.option_count ?? 0, { headerKey: "column.virtual-part.options", priority: 4 }),
      c.number("line_count", (row) => row.line_count ?? 0, { headerKey: "column.virtual-part.lines", priority: 4 }),
    ];
  }, [t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={title} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        subtitle={t(
          "virtual-part.subtitle",
          "What engineering BOMs consume when any of several real parts will do. A panel is a substrate, a thickness and a finish, consumed by area and cut from one of several real panels at an expected yield.",
        )}
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              {t("virtual-part.new", "New virtual panel")}
            </Button>
          ) : null
        }
      />
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <div className="flex min-h-80 min-w-0 flex-1 flex-col">
          <DataTable<VirtualPartDTO>
            data={rows}
            columns={columns}
            loading={list.isLoading}
            getRowId={(row) => String(row.id)}
            onRowClick={(row) => setSelectedId(row.id)}
            rowClassName={(row) => (row.id === selected?.id ? "bg-accent-soft" : undefined)}
            stateKey="config.virtual-part"
            emptyMessage={t("virtual-part.empty", "No virtual parts yet. The engineering BOM build creates the panels from SIGM's wood BOMs.")}
            editor={
              canWrite
                ? {
                    fields: {
                      // Renamed in place: BOM lines and lots point at the part, so they follow.
                      part_number: {
                        kind: "text",
                        get: (row) => row.part_number ?? "",
                        set: (row, value) => ({ ...row, part_number: value }),
                        required: true,
                      },
                      part_name: {
                        kind: "text",
                        get: (row) => row.part_name ?? "",
                        set: (row, value) => ({ ...row, part_name: value }),
                        required: true,
                      },
                      // Typed as a percentage; stored as a share.
                      expected_yield: {
                        kind: "number",
                        get: (row) => Math.round(Number(row.expected_yield) * 1000) / 10,
                        set: (row, value) => ({ ...row, expected_yield: value === "" ? 0 : Number(value) / 100 }),
                        required: true,
                      },
                    },
                    validate: (row) => {
                      const errors: Record<string, string> = {};
                      const value = Number(row.expected_yield);
                      if (!(Number.isFinite(value) && value >= 0.5 && value <= 1)) {
                        errors.expected_yield = t("virtual-part.yield.range", "Between 50 and 100 %.");
                      }
                      if (!(row.part_number ?? "").trim()) errors.part_number = t("validation.required", "Required");
                      if (!(row.part_name ?? "").trim()) errors.part_name = t("validation.required", "Required");
                      return Object.keys(errors).length === 0 ? null : errors;
                    },
                    onSave: async (row) => {
                      try {
                        await panels.update(row);
                      } catch (failure) {
                        throw new Error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
                      }
                      await queryClient.invalidateQueries({ queryKey: PANELS_KEY });
                      toast.success(t("notification.saved", "Saved"));
                    },
                  }
                : undefined
            }
          />
        </div>
        <aside className="shrink-0 overflow-y-auto border-t border-line bg-surface lg:w-[30rem] lg:border-t-0 lg:border-l">
          {selected ? (
            <PanelDetail key={selected.id} panel={selected} canWrite={canWrite} onDeleted={() => setSelectedId(null)} />
          ) : (
            <p className="p-4 text-[13px] text-ink-muted lg:p-6">
              {t("virtual-part.pick", "Pick a virtual part to see the real parts it can be consumed as.")}
            </p>
          )}
        </aside>
      </div>
      {creating && (
        <CreateDialog
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false);
            setSelectedId(created.id);
          }}
        />
      )}
    </>
  );
}

/**
 * One virtual panel's real panels: their sheet size and status, which one BOM
 * lines start from, adding one (a part search) and removing one.
 */
/**
 * A virtual panel's real panels on its part page: the same detail as the side
 * panel here (default panel, options, yield), found by the VIRTUAL part's id.
 */
export function VirtualPartRealParts({ partId, canWrite, onDeleted }: { partId: number; canWrite: boolean; onDeleted: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const panels = useMemo(() => new VirtualPartClient(http), [http]);
  const panel = useQuery({
    queryKey: [...PANELS_KEY, "part", partId],
    queryFn: async () => (await panels.find({ filters: { part_id: [String(partId)] }, size: 1 })).content[0] ?? null,
  });
  if (panel.isLoading) {
    return <p className="p-4 text-[13px] text-ink-muted">{t("common.loading", "Loading")}</p>;
  }
  if (!panel.data) {
    return (
      <p className="p-4 text-[13px] text-ink-muted">
        {t("virtual-part.part.missing", "This part is virtual but has no virtual part description: rerun the engineering BOM build.")}
      </p>
    );
  }
  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto p-4 lg:p-6">
      <PanelDetail panel={panel.data} canWrite={canWrite} onDeleted={onDeleted} />
    </div>
  );
}

function PanelDetail({
  panel,
  canWrite,
  onDeleted,
}: {
  panel: VirtualPartDTO;
  canWrite: boolean;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const panels = useMemo(() => new VirtualPartClient(http), [http]);
  const optionClient = useMemo(() => new VirtualPartOptionClient(http), [http]);

  const options = useQuery({
    queryKey: optionsKey(panel.part_id),
    queryFn: () => optionClient.forVirtualPart(panel.part_id),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: PANELS_KEY });
    await queryClient.invalidateQueries({ queryKey: optionsKey(panel.part_id) });
  };
  const failed = (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));

  const setDefault = useMutation({
    mutationFn: (realPartId: number | null) => panels.update({ ...panel, default_real_part_id: realPartId }),
    onSuccess: async () => {
      await refresh();
      toast.success(t("notification.saved", "Saved"));
    },
    onError: failed,
  });

  const add = useMutation({
    mutationFn: (part: PartGridDTO) =>
      optionClient.create({
        id: 0,
        virtual_part_id: panel.part_id,
        real_part_id: part.id,
        position: Math.max(0, ...(options.data ?? []).map((option) => option.position)) + 10,
      }),
    onSuccess: async () => {
      await refresh();
      toast.success(t("virtual-part.option.added", "Real panel added"));
    },
    onError: failed,
  });

  const remove = useMutation({
    mutationFn: (option: VirtualPartOptionDTO) => optionClient.remove(option.id),
    onSuccess: async () => {
      await refresh();
      toast.success(t("notification.deleted", "Deleted"));
    },
    onError: failed,
  });

  const removePanel = useMutation({
    mutationFn: () => panels.remove(panel.id),
    onSuccess: async () => {
      onDeleted();
      await queryClient.invalidateQueries({ queryKey: PANELS_KEY });
      toast.success(t("notification.deleted", "Deleted"));
    },
    onError: failed,
  });

  const columns = useMemo(() => {
    const c = defineColumns<VirtualPartOptionDTO>();
    return [
      c.custom(
        "real_part_number",
        (row) => row.real_part_number ?? "",
        (row) => (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate">
              <span className="font-medium">{row.real_part_number}</span>
              <span className="ml-2 text-[11px] text-ink-faint">{row.real_part_name}</span>
            </span>
            {row.real_part_id === panel.default_real_part_id && (
              <Badge variant="accent" size="sm">
                {t("virtual-part.option.default", "Default")}
              </Badge>
            )}
          </span>
        ),
        { headerKey: "column.virtual-part.real-panel", priority: 1 },
      ),
      c.text("conversion_factor", (row) => (row.conversion_factor == null ? "" : formatNumber(row.conversion_factor)), {
        headerKey: "column.virtual-part.factor",
        priority: 4,
      }),
      c.text(
        "size",
        (row) =>
          row.length_mm != null && row.width_mm != null ? `${formatNumber(row.length_mm)} x ${formatNumber(row.width_mm)} mm` : "",
        { headerKey: "column.virtual-part.sheet-size", priority: 2 },
      ),
      c.custom(
        "real_part_status",
        (row) => row.real_part_status ?? "",
        (row) =>
          row.real_part_status === "INACTIVE" ? (
            <Badge variant="neutral" size="sm">
              {t("status.inactive", "Inactive")}
            </Badge>
          ) : (
            <span />
          ),
        { headerKey: "column.status", priority: 3 },
      ),
    ];
  }, [panel.default_real_part_id, t]);

  const defaultChoices = (options.data ?? []).map((option) => ({
    value: String(option.real_part_id),
    label: option.real_part_number ?? String(option.real_part_id),
  }));

  return (
    <div className="flex flex-col gap-4 p-4 lg:p-6">
      <div>
        <h2 className="text-[15px] font-semibold text-ink">{panel.part_number}</h2>
        <p className="mt-0.5 text-[12px] text-ink-muted">{panel.part_name}</p>
        <p className="mt-1 text-[12px] text-ink-muted">
          {t("virtual-part.summary", "{{substrate}}, {{thickness}} mm, {{finish}} · yield {{yield}} · {{lines}} BOM lines", {
            substrate: panel.substrate ? substrateLabel(panel.substrate, t) : kindLabel(panel.kind, t),
            thickness: panel.thickness_mm == null ? "" : formatNumber(panel.thickness_mm),
            finish: panel.finish ?? "",
            yield: yieldLabel(panel.expected_yield),
            lines: panel.line_count ?? 0,
          })}
        </p>
      </div>

      <Field
        label={t("virtual-part.default-real", "Default real panel")}
        htmlFor="virtual-panel-default"
        hint={t("virtual-part.default-real.hint", "What a BOM line using this virtual panel is cut from unless the line names another option.")}
      >
        <Select
          id="virtual-panel-default"
          value={panel.default_real_part_id == null ? "" : String(panel.default_real_part_id)}
          disabled={!canWrite || setDefault.isPending}
          options={[{ value: "", label: t("virtual-part.default-real.none", "None") }, ...defaultChoices]}
          onChange={(event) => setDefault.mutate(event.target.value === "" ? null : Number(event.target.value))}
        />
      </Field>

      <div className="flex flex-col gap-2">
        <h3 className="text-[13px] font-semibold text-ink">{t("virtual-part.options", "Real panels it can be cut from")}</h3>
        <div className="flex max-h-96 min-h-32 flex-col rounded-md border border-line">
          <DataTable<VirtualPartOptionDTO>
            data={options.data ?? []}
            columns={columns}
            loading={options.isLoading}
            getRowId={(row) => String(row.id)}
            hidePrint
            rowActions={(row) => (
              <span className="flex items-center gap-1">
                <OpenRecord target={{ kind: "part", id: row.real_part_id }} label={row.real_part_number ?? ""} />
                {canWrite && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t("virtual-part.option.remove", "Remove")}
                    title={t("virtual-part.option.remove", "Remove")}
                    disabled={remove.isPending}
                    onClick={async (event) => {
                      event.stopPropagation();
                      if (
                        await confirm({
                          title: t("virtual-part.option.remove.title", "Remove {{part}} from the options?", { part: row.real_part_number ?? "" }),
                          message:
                            row.real_part_id === panel.default_real_part_id
                              ? t("virtual-part.option.remove.default", "It is the default real panel: pick another default first.")
                              : undefined,
                          confirmLabel: t("virtual-part.option.remove", "Remove"),
                          variant: "danger",
                        })
                      ) {
                        remove.mutate(row);
                      }
                    }}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </span>
            )}
            emptyMessage={t("virtual-part.options.empty", "No real panel yet: purchasing cannot turn this area into sheets.")}
          />
        </div>
        {canWrite && (
          <PartPicker
            placeholder={t("virtual-part.option.add", "Add a real panel: search by number or name")}
            disabled={add.isPending}
            exclude={[panel.part_id, ...(options.data ?? []).map((option) => option.real_part_id)]}
            onPick={(part) => add.mutate(part)}
          />
        )}
      </div>

      {canWrite && (
        <div className="border-t border-line pt-3">
          <Button
            variant="ghost"
            disabled={removePanel.isPending}
            onClick={async () => {
              if (
                await confirm({
                  title: t("virtual-part.delete.title", "Delete {{part}}?", { part: panel.part_number ?? "" }),
                  message:
                    (panel.line_count ?? 0) > 0
                      ? t("virtual-part.delete.used", "{{n}} BOM lines use it: the server refuses until they no longer do.", {
                          n: panel.line_count ?? 0,
                        })
                      : t("virtual-part.delete.message", "The virtual panel and its options are deleted."),
                  confirmLabel: t("virtual-part.delete", "Delete virtual panel"),
                  variant: "danger",
                })
              ) {
                removePanel.mutate();
              }
            }}
          >
            <Trash2 className="size-4" />
            {t("virtual-part.delete", "Delete virtual panel")}
          </Button>
        </div>
      )}
    </div>
  );
}

/** A new virtual panel from its three keys; the server makes and numbers the part. */
function CreateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (created: VirtualPartDTO) => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const panels = useMemo(() => new VirtualPartClient(http), [http]);
  const [substrate, setSubstrate] = useState<PanelSubstrate>("PARTICLE");
  const [thickness, setThickness] = useState("");
  const [finish, setFinish] = useState("");
  const [yieldPercent, setYieldPercent] = useState("82");

  const thicknessValue = Number(thickness);
  const yieldValue = Number(yieldPercent);
  const thicknessValid = thickness.trim() !== "" && Number.isFinite(thicknessValue) && thicknessValue > 0;
  const yieldValid = yieldPercent.trim() !== "" && Number.isFinite(yieldValue) && yieldValue >= 50 && yieldValue <= 100;
  const valid = thicknessValid && finish.trim() !== "" && yieldValid;

  const create = useMutation({
    mutationFn: () =>
      panels.create({
        id: 0,
        part_id: 0,
        kind: "PANEL",
        substrate,
        thickness_mm: thicknessValue,
        finish: finish.trim().toUpperCase(),
        expected_yield: yieldValue / 100,
        default_real_part_id: null,
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: PANELS_KEY });
      toast.success(t("virtual-part.created", "{{part}} created", { part: created.part_number ?? "" }));
      onCreated(created);
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("virtual-part.new", "New virtual panel")}
      description={t("virtual-part.new.description", "The part is created and numbered from these: V-PAR-16-BLANC for particleboard, 16 mm, white.")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid} loading={create.isPending} onClick={() => create.mutate()}>
            {t("button.create", "Create")}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("column.virtual-part.substrate", "Substrate")} htmlFor="vp-substrate" required>
          <Select
            id="vp-substrate"
            value={substrate}
            options={PANEL_SUBSTRATES.map((value) => ({ value, label: substrateLabel(value, t) }))}
            onChange={(event) => setSubstrate(event.target.value as PanelSubstrate)}
          />
        </Field>
        <Field
          label={t("virtual-part.thickness-mm", "Thickness (mm)")}
          htmlFor="vp-thickness"
          required
          {...(thickness !== "" && !thicknessValid ? { error: t("validation.positive", "Must be greater than zero.") } : {})}
        >
          <Input
            id="vp-thickness"
            type="number"
            min="0"
            step="any"
            value={thickness}
            invalid={thickness !== "" && !thicknessValid}
            onChange={(event) => setThickness(event.target.value)}
          />
        </Field>
        <Field
          label={t("column.virtual-part.finish", "Finish")}
          htmlFor="vp-finish"
          required
          hint={t("virtual-part.finish.hint", "As the plant names it: BLANC, NOIR, BRUT.")}
        >
          <Input id="vp-finish" value={finish} onChange={(event) => setFinish(event.target.value)} />
        </Field>
        <Field
          label={t("virtual-part.yield-percent", "Expected yield (%)")}
          htmlFor="vp-yield"
          required
          {...(!yieldValid ? { error: t("virtual-part.yield.range", "Between 50 and 100 %.") } : {})}
        >
          <Input
            id="vp-yield"
            type="number"
            min="50"
            max="100"
            step="any"
            value={yieldPercent}
            invalid={!yieldValid}
            onChange={(event) => setYieldPercent(event.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}
